import React, { useEffect, useMemo, useState } from 'react';
import { 
  Download, 
  Pencil, 
  Eye, 
  Search,
  RefreshCw,
  TrendingUp,
  Clock,
  CheckCircle,
  Package,
  DollarSign,
  ArrowUpRight,
  ArrowDownRight,
  MoreHorizontal,
  FileText,
  XCircle,
} from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { formatMoney as fmtMoney, normalizeCurrencyRates } from '../../../util/money';
import { getCountryName } from '../../../util/countryNames';
import API from '../../../util/API';
import { fetchOrders } from '../../../features/orderSlice';
import {
  normalizeCurrencyCode,
} from '../../../util/systemCurrency';
import { resolveOrderUsdValue } from '../../../util/orderCurrency';
import {
  downloadOrderInvoice,
  openOrderInvoice,
} from '../../../util/orderInvoiceAccess';

const cx = (...arr) => arr.filter(Boolean).join(" ");

const Card = ({ children, className }) => (
  <div className={cx("bg-white border border-slate-200 rounded-2xl", className)}>{children}</div>
);

const statusConfig = {
  Delivered: { icon: CheckCircle, cls: 'bg-emerald-50 text-emerald-700 border-emerald-100' },
  Cancelled: { icon: XCircle, cls: 'bg-slate-100 text-slate-500 border-slate-200' },
  Default: { icon: Clock, cls: 'bg-amber-50 text-amber-700 border-amber-100' },
};

const normalizeTrackingValue = (value) => String(value ?? '').trim().toLowerCase();

const summarizeTrackingMap = (map) => {
  const records = Array.isArray(map?.tracking_records) ? map.tracking_records : [];
  const hasTrackingId = Array.isArray(map?.order_ids) && map.order_ids.length > 0;
  const hasDelivered = records.some((record) => {
    const events = Array.isArray(record?.tracking_events) ? record.tracking_events : [];
    return events.some((event) => normalizeTrackingValue(event?.tracking_name) === 'delivered');
  });
  const hasLogistics = records.some((record) => (
    Boolean(record?.carrier_name)
    || Boolean(record?.eta_time_stamps)
    || (Array.isArray(record?.tracking_events) && record.tracking_events.length > 0)
  ));

  return {
    hasTrackingId,
    hasLogistics,
    hasDelivered,
  };
};

const getOrderTrackingFlags = (order, trackingSummary) => {
  const raw = order || {};
  const items = Array.isArray(raw?.items) ? raw.items : [];
  const fallbackHasTrackingId = Boolean(raw?.externalOutOrderId)
    || (Array.isArray(raw?.externalOrderIds) && raw.externalOrderIds.length > 0)
    || items.some((item) => Boolean(item?.ali_order_id));
  const fallbackHasLogistics = Boolean(raw?.isShipped)
    || Boolean(raw?.tracking?.carrier)
    || Boolean(raw?.tracking?.lastStatus)
    || Boolean(raw?.tracking?.trackingNumber)
    || Boolean(raw?.externalStatus);
  const fallbackHasDelivered = Boolean(raw?.isDelivered);

  const hasTrackingId = trackingSummary?.hasTrackingId ?? fallbackHasTrackingId;
  const hasAliExpressLogistics = trackingSummary?.hasLogistics ?? fallbackHasLogistics;
  const hasAliExpressDelivered = trackingSummary?.hasDelivered ?? fallbackHasDelivered;
  const isApproved = Boolean(raw?.isApproved);
  const approavedDisplay = isApproved || hasTrackingId || hasAliExpressLogistics || hasAliExpressDelivered;
  const aliExpressHasProgress = Boolean(raw?.externalOutOrderId)
    || (Array.isArray(raw?.externalOrderIds) && raw.externalOrderIds.length > 0)
    || Boolean(raw?.externalStatus)
    || hasTrackingId
    || approavedDisplay;
  const aliExpressIsGreen = hasAliExpressLogistics || hasAliExpressDelivered;

  return {
    approavedDisplay,
    aliExpressHasProgress,
    aliExpressIsGreen,
    hasAliExpressLogistics,
    hasAliExpressDelivered,
  };
};

const getLatestStatus = (order, trackingSummary) => {
  const raw = order?.raw || order;
  if (String(raw?.order_status || '') === 'cancelled' || raw?.cancelledAt) return 'Cancelled';
  const flags = getOrderTrackingFlags(order?.raw || order, trackingSummary);
  if (flags.hasAliExpressDelivered) return 'Delivered';
  if (flags.hasAliExpressLogistics) return 'Logestic';
  if (flags.aliExpressHasProgress) return 'AliExpress';
  return 'Not Approaved';
};

const getOrderCurrency = (order) => {
  const directCurrency = normalizeCurrencyCode(
    order?.paidCurrency ||
    order?.paymentResult?.currency ||
    order?.currency ||
    order?.currencyCode ||
    order?.totalCurrency ||
    order?.paymentCurrency,
  );
  if (directCurrency) return directCurrency;

  const items = Array.isArray(order?.items) ? order.items : [];
  for (const item of items) {
    const itemCurrency = normalizeCurrencyCode(item?.currency);
    if (itemCurrency) return itemCurrency;
  }

  return 'USD';
};

const getOrderShipToCountry = (order) => {
  const countryCode = String(order?.shippingAddress?.countryCode || '').trim().toUpperCase();
  const countryName = String(order?.shippingAddress?.country || '').trim();

  if (countryCode && countryName && countryName.toUpperCase() !== countryCode) {
    return `${countryName} (${countryCode})`;
  }
  if (countryName) return countryName;
  if (countryCode) return getCountryName(countryCode);
  return '—';
};

const AllOrders = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [viewMode, setViewMode] = useState('table'); // table or grid
  const [trackingSummaryByOrderId, setTrackingSummaryByOrderId] = useState({});
  const [pdfLoadingByOrderId, setPdfLoadingByOrderId] = useState({});
  const [cancelingOrderId, setCancelingOrderId] = useState('');
  const { orders = [], status: ordersStatus, error: ordersError } = useSelector((state) => state.order || {});

  const currencyPayload = useSelector((state) => state.currency?.currency_list);
  const rates = useMemo(() => normalizeCurrencyRates(currencyPayload), [currencyPayload]);
  const effectiveCurrency = 'USD';
  const money = (amount) => fmtMoney(amount, { from: 'USD', to: 'USD' });
  const orderMoney = (amount, currency = 'USD') => {
    const code = normalizeCurrencyCode(currency) || 'USD';
    return fmtMoney(amount, { from: code, to: code });
  };

  useEffect(() => {
    if (ordersStatus === 'idle') dispatch(fetchOrders());
  }, [dispatch, ordersStatus]);

  useEffect(() => {
    const list = Array.isArray(orders) ? orders : [];
    if (!list.length) return undefined;

    const targets = list.map((o) => {
      const mongoId = String(o?._id || o?.id || '').trim();
      const orderId = String(o?.orderId || '').trim();
      const outOrderId = String(o?.externalOutOrderId || '').trim();
      return { mongoId, orderId, outOrderId };
    }).filter((t) => t.mongoId || t.orderId || t.outOrderId);

    const pending = targets.filter((t) => !trackingSummaryByOrderId[t.mongoId]);
    if (!pending.length) return undefined;

    let alive = true;
    Promise.allSettled(
      pending.map((t) => API.get('/aliexpress-tracking/map', {
        params: {
          orderMongoId: t.mongoId || undefined,
          orderId: t.orderId || undefined,
          out_order_id: t.outOrderId || undefined,
        }
      }))
    ).then((results) => {
      if (!alive) return;
      const updates = {};
      results.forEach((result, idx) => {
        if (result.status !== 'fulfilled') return;
        const data = result.value?.data?.data || null;
        if (!data) return;
        const key = pending[idx]?.mongoId || '';
        if (!key) return;
        updates[key] = summarizeTrackingMap(data);
      });
      if (Object.keys(updates).length) {
        setTrackingSummaryByOrderId((prev) => ({ ...prev, ...updates }));
      }
    });

    return () => {
      alive = false;
    };
  }, [orders, trackingSummaryByOrderId]);

  const normalizedOrders = useMemo(() => {
    const list = Array.isArray(orders) ? orders : [];
    return list.map((o) => {
      const mongoId = String(o?._id || o?.id || '');
      const displayId = String(o?.orderId || o?._id || o?.id || '');
      const createdAt = o?.createdAt ? new Date(o.createdAt) : null;
      const date = createdAt && !Number.isNaN(createdAt.getTime())
        ? createdAt.toISOString().slice(0, 10)
        : (o?.date || '');

      const customer =
        o?.shippingAddress?.fullName ||
        o?.billingAddress?.fullName ||
        (typeof o?.user === 'object' ? o?.user?.name : '') ||
        (typeof o?.user === 'string' ? o.user : '') ||
        '—';

      const email =
        o?.paymentResult?.email_address ||
        (typeof o?.user === 'object' ? o?.user?.email : '') ||
        '';

      const phone = o?.shippingAddress?.phone || o?.billingAddress?.phone || '';

      const itemQty = Array.isArray(o?.items)
        ? o.items.reduce((sum, it) => sum + (Number(it?.quantity) || 0), 0)
        : 0;
      const itemsCount = itemQty || (Array.isArray(o?.items) ? o.items.length : 0);

      const total = typeof o?.totalPrice === 'number' ? o.totalPrice : (Number(o?.total) || 0);
      const auditedBaseTotal = o?.accountingUsd?.amount;
      const baseTotalCandidate =
        auditedBaseTotal !== null &&
        auditedBaseTotal !== undefined &&
        auditedBaseTotal !== ''
          ? auditedBaseTotal
          : o?.baseTotalPrice;
      const baseTotal = Number.isFinite(Number(baseTotalCandidate))
        ? Number(baseTotalCandidate)
        : null;
      const currency = getOrderCurrency(o);
      const shipToCountry = getOrderShipToCountry(o);

      const trackingSummary = trackingSummaryByOrderId[mongoId];
      const status = getLatestStatus(o, trackingSummary);
      const payment = o?.paymentMethod || o?.payment || '';
      const cancelled = String(o?.order_status || '') === 'cancelled' || Boolean(o?.cancelledAt);

      return {
        raw: o,
        id: displayId,
        mongoId,
        cancelled,
        cancellationReason: o?.cancellationReason || '',
        cancelledAt: o?.cancelledAt || '',
        customer,
        email,
        phone,
        date,
        status,
        total,
        baseTotal,
        currency,
        shipToCountry,
        items: itemsCount,
        payment,
      };
    });
  }, [orders, trackingSummaryByOrderId]);

  const getUsdEquivalent = React.useCallback((order) => {
    return resolveOrderUsdValue(order?.raw || order, rates).amount;
  }, [rates]);

  const getUsdBaseLabel = (order) => {
    const resolved = resolveOrderUsdValue(order?.raw || order, rates);
    if (!Number.isFinite(resolved.amount)) return 'Historical USD rate unavailable';
    const suffix =
      resolved.source === 'current_rate_estimate'
        ? 'estimate'
        : resolved.source === 'manual_correction'
          ? 'corrected'
          : 'base';
    return `${money(resolved.amount)} ${suffix}`;
  };


  const filteredOrders = useMemo(() => {
    const q = search.trim().toLowerCase();

    const now = new Date();
    const isWithinDays = (createdAt, days) => {
      if (!createdAt) return false;
      const ms = days * 24 * 60 * 60 * 1000;
      return createdAt.getTime() >= (now.getTime() - ms);
    };

    return normalizedOrders.filter((order) => {
      const matchesSearch = !q ||
        String(order.id).toLowerCase().includes(q) ||
        String(order.customer).toLowerCase().includes(q) ||
        String(order.email).toLowerCase().includes(q) ||
        String(order.phone).toLowerCase().includes(q);

      const matchesStatus = !statusFilter || order.status === statusFilter;

      let matchesDate = true;
      if (dateFilter) {
        const createdAt = order.raw?.createdAt ? new Date(order.raw.createdAt) : null;
        if (dateFilter === 'today') {
          matchesDate = !!createdAt && createdAt.toDateString() === now.toDateString();
        } else if (dateFilter === '7d') {
          matchesDate = isWithinDays(createdAt, 7);
        } else if (dateFilter === '30d') {
          matchesDate = isWithinDays(createdAt, 30);
        } else {
          matchesDate = true;
        }
      }

      return matchesSearch && matchesStatus && matchesDate;
    });
  }, [dateFilter, normalizedOrders, search, statusFilter]);

  const totalOrders = normalizedOrders.length;
  const hasActiveFilters = Boolean(search.trim() || statusFilter || dateFilter);

  const handleClearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setDateFilter('');
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedOrders(filteredOrders.map(order => order.mongoId || order.id));
    } else {
      setSelectedOrders([]);
    }
  };

  const handleSelectOrder = (orderId, checked) => {
    if (checked) {
      setSelectedOrders([...selectedOrders, orderId]);
    } else {
      setSelectedOrders(selectedOrders.filter(id => id !== orderId));
    }
  };

  const goToOrderDetails = (order) => {
    const targetId = String(order?.mongoId || order?.id || '').trim();
    if (!targetId) return;
    navigate(`/administrator/orders/details/${targetId}`);
  };

  const setPdfLoading = (orderKey, kind, value) => {
    if (!orderKey) return;
    setPdfLoadingByOrderId((prev) => ({
      ...prev,
      [orderKey]: {
        ...(prev[orderKey] || {}),
        [kind]: value,
      },
    }));
  };

  const normalizeErrorMessage = (value) => {
    if (value == null) return '';
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);

    if (Array.isArray(value)) {
      const parts = value.map((item) => normalizeErrorMessage(item)).filter(Boolean);
      return parts.join(', ');
    }

    if (typeof value === 'object') {
      const direct = normalizeErrorMessage(value?.message) || normalizeErrorMessage(value?.error);
      if (direct) return direct;
      try {
        return JSON.stringify(value);
      } catch (_err) {
        return '';
      }
    }

    return '';
  };

  const readPdfErrorMessage = async (error) => {
    const blob = error?.response?.data;
    if (blob instanceof Blob) {
      try {
        const text = await blob.text();
        try {
          const parsed = JSON.parse(text);
          const parsedMessage = normalizeErrorMessage(parsed);
          if (parsedMessage) return parsedMessage;
        } catch (_jsonErr) {
          const plain = normalizeErrorMessage(text);
          if (plain) return plain;
        }
        return 'Failed to generate PDF';
      } catch (_parseErr) {
        return 'Failed to generate PDF';
      }
    }
    return normalizeErrorMessage(error?.response?.data)
      || normalizeErrorMessage(error?.message)
      || 'Failed to generate PDF';
  };

  const handleViewPdf = async (order) => {
    const orderKey = String(order?.mongoId || order?.id || '').trim();
    if (!orderKey) return;

    setPdfLoading(orderKey, 'view', true);
    try {
      await openOrderInvoice(orderKey);
    } catch (error) {
      const message = await readPdfErrorMessage(error);
      window.alert(message);
    } finally {
      setPdfLoading(orderKey, 'view', false);
    }
  };

  const handleDownloadPdf = async (order) => {
    const orderKey = String(order?.mongoId || order?.id || '').trim();
    if (!orderKey) return;

    setPdfLoading(orderKey, 'download', true);
    try {
      const fallbackName = `order-${String(order?.id || orderKey)}.pdf`;
      await downloadOrderInvoice(orderKey, fallbackName);
    } catch (error) {
      const message = await readPdfErrorMessage(error);
      window.alert(message);
    } finally {
      setPdfLoading(orderKey, 'download', false);
    }
  };

  const handleCancelOrder = async (order) => {
    const orderKey = String(order?.mongoId || order?.id || '').trim();
    if (!orderKey) return;

    const reason = window.prompt(
      `Cancel order ${order?.id || orderKey}? A cancellation reason is required.`,
      ''
    );
    if (reason == null) return;
    const trimmed = String(reason || '').trim();
    if (!trimmed) {
      window.alert('A cancellation reason is required.');
      return;
    }
    const confirmed = window.confirm(
      `Cancel order ${order?.id || orderKey} with reason "${trimmed}"?`
    );
    if (!confirmed) return;

    setCancelingOrderId(orderKey);
    try {
      const res = await API.post(`/order/${orderKey}/cancel`, { reason: trimmed });
      window.alert(res?.data?.message || 'Order cancelled.');
    } catch (error) {
      window.alert(
        normalizeErrorMessage(error?.response?.data)
        || normalizeErrorMessage(error?.message)
        || 'Failed to cancel order'
      );
    } finally {
      setCancelingOrderId('');
      dispatch(fetchOrders());
    }
  };

  const analytics = useMemo(() => {
    const list = normalizedOrders;
    const total = list.length;
    const totalItems = list.reduce((sum, o) => sum + (Number(o.items) || 0), 0);
    const byStatus = (s) => list.filter((o) => o.status === s).length;
    const paidOrders = list.filter((order) => order?.raw?.isPaid === true);
    const resolvedPaidOrders = paidOrders.filter((order) =>
      Number.isFinite(getUsdEquivalent(order))
    );
    const revenue = resolvedPaidOrders.reduce((sum, o) => {
      const normalized = getUsdEquivalent(o);
      return sum + (Number.isFinite(normalized) ? normalized : 0);
    }, 0);
    const estimatedPaidUsd = paidOrders.filter(
      (order) =>
        resolveOrderUsdValue(order?.raw || order, rates).source ===
        'current_rate_estimate'
    ).length;
    const delivered = byStatus('Delivered');
    const pending = Math.max(0, total - delivered);

    return {
      total,
      pending,
      notApproved: byStatus('Not Approaved'),
      aliExpress: byStatus('AliExpress'),
      logestic: byStatus('Logestic'),
      delivered,
      revenue,
      avgOrderValue: resolvedPaidOrders.length
        ? revenue / resolvedPaidOrders.length
        : 0,
      unresolvedPaidUsd: paidOrders.length - resolvedPaidOrders.length,
      estimatedPaidUsd,
      totalItems,
    };
  }, [normalizedOrders, getUsdEquivalent, rates]);

  const MetricTile = ({ title, value, change, trend, icon: Icon, tone }) => (
    <Card className="p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">{title}</p>
          <p className="mt-2 text-2xl font-black tracking-tight text-slate-900">{value}</p>
          {change ? (
            <div className={cx(
              "mt-2 inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[11px] font-semibold",
              trend === "up" ? "bg-emerald-50 text-emerald-700 border-emerald-100" : "bg-rose-50 text-rose-700 border-rose-100"
            )}>
              {trend === 'up' ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
              {change}
            </div>
          ) : null}
        </div>

        <div className={cx("h-10 w-10 rounded-2xl border flex items-center justify-center shrink-0", tone)}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </Card>
  );

  const OrderCard = ({ order }) => {
    const status = statusConfig[order.status] || statusConfig.Default;
    const StatusIcon = status?.icon || Clock;
    const orderKey = order.mongoId || order.id;
    const pdfState = pdfLoadingByOrderId[orderKey] || {};
    const isViewPdfLoading = Boolean(pdfState.view);
    const isDownloadPdfLoading = Boolean(pdfState.download);

    return (
      <Card className="p-6 hover:border-slate-300 transition-colors">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              checked={selectedOrders.includes(order.mongoId || order.id)}
              onChange={(e) => handleSelectOrder(order.mongoId || order.id, e.target.checked)}
              className="w-4 h-4 text-primary bg-white border-slate-300 rounded focus:ring-primary"
            />
            <div>
              <h3 className="font-semibold text-gray-900">{order.id}</h3>
              <p className="text-sm text-gray-500">{order.date}</p>
            </div>
          </div>
          <div className={`px-3 py-1 rounded-full text-xs font-semibold border ${status.cls} flex items-center space-x-1`}>
            <StatusIcon className="w-3 h-3" />
            <span>{order.status}</span>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <p className="font-medium text-gray-900">{order.customer}</p>
            <p className="text-sm text-gray-500">{order.email}</p>
            <p className="text-sm text-gray-500">{order.phone}</p>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total</p>
              <p className="font-semibold text-gray-900">{orderMoney(order.total, order.currency || 'USD')}</p>
              <p className="text-xs text-gray-500">{getUsdBaseLabel(order)}</p>
              <p className="text-xs text-gray-500 mt-0.5">Ship to {order.shipToCountry}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Items</p>
              <p className="font-semibold text-gray-900">{order.items}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Payment</p>
              <p className="font-semibold text-gray-900">{order.payment}</p>
            </div>
          </div>

          <div className="pt-3 border-t border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex space-x-2">
                <button
                  className="p-2 text-slate-700 hover:bg-slate-100 rounded-xl transition-colors"
                  title="View"
                  onClick={() => goToOrderDetails(order)}
                >
                  <span className="sr-only">View</span>
                  <Eye className="w-4 h-4" />
                </button>
                <button className="p-2 text-slate-700 hover:bg-slate-100 rounded-xl transition-colors" title="Edit">
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  className={cx(
                    "p-2 rounded-xl transition-colors",
                    isViewPdfLoading ? "text-slate-400 bg-slate-100 cursor-not-allowed" : "text-slate-700 hover:bg-slate-100"
                  )}
                  title="View PDF"
                  onClick={() => handleViewPdf(order)}
                  disabled={isViewPdfLoading}
                >
                  {isViewPdfLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                </button>
                <button
                  className={cx(
                    "p-2 rounded-xl transition-colors",
                    isDownloadPdfLoading ? "text-slate-400 bg-slate-100 cursor-not-allowed" : "text-slate-700 hover:bg-slate-100"
                  )}
                  title="Download PDF"
                  onClick={() => handleDownloadPdf(order)}
                  disabled={isDownloadPdfLoading}
                >
                  {isDownloadPdfLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                </button>
                <button
                  className={cx(
                    "p-2 rounded-xl transition-colors",
                    order.cancelled || cancelingOrderId === (order.mongoId || order.id)
                      ? "text-slate-400 bg-slate-100 cursor-not-allowed"
                      : "text-rose-600 hover:bg-rose-50"
                  )}
                  title={order.cancelled ? `Already cancelled${order.cancellationReason ? `: ${order.cancellationReason}` : ''}` : "Cancel order"}
                  onClick={() => handleCancelOrder(order)}
                  disabled={order.cancelled || cancelingOrderId === (order.mongoId || order.id)}
                >
                  <XCircle className="w-4 h-4" />
                </button>
              </div>
              <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors" title="More">
                <MoreHorizontal className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-end gap-2">
        <button
          onClick={() => dispatch(fetchOrders())}
          disabled={ordersStatus === 'loading'}
          className={cx(
            "px-4 py-2 rounded-2xl bg-slate-900 text-white text-sm font-semibold hover:bg-primary transition-colors inline-flex items-center gap-2",
            ordersStatus === 'loading' ? 'opacity-60 cursor-not-allowed' : ''
          )}
        >
          <RefreshCw className={cx("w-4 h-4", ordersStatus === 'loading' ? 'animate-spin' : '')} />
          Refresh
        </button>
        <button className="px-4 py-2 rounded-2xl border border-slate-200 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors inline-flex items-center gap-2">
          <Download className="w-4 h-4" />
          Export
        </button>
      </div>

      {ordersStatus === 'failed' ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-800">
          Failed to load orders: {typeof ordersError === 'string' ? ordersError : (ordersError?.error || 'Unknown error')}
        </div>
      ) : null}

      {/* Analytics */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <MetricTile
            title="Total Orders"
            value={analytics.total}
            icon={FileText}
            tone="bg-slate-50 text-slate-700 border-slate-100"
          />
          <MetricTile
            title="Paid Revenue (USD)"
            value={money(analytics.revenue, effectiveCurrency)}
            icon={DollarSign}
            tone="bg-emerald-50 text-emerald-700 border-emerald-100"
          />
          <MetricTile
            title="Pending Orders"
            value={analytics.pending}
            icon={Clock}
            tone="bg-amber-50 text-amber-700 border-amber-100"
          />
          <MetricTile
            title="Avg Paid Order (USD)"
            value={money(analytics.avgOrderValue, effectiveCurrency)}
            icon={TrendingUp}
            tone="bg-violet-50 text-violet-700 border-violet-100"
          />
      </div>

      {analytics.unresolvedPaidUsd > 0 ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {analytics.unresolvedPaidUsd} paid legacy order(s) are excluded from exact USD revenue because their historical exchange rate was not stored.
        </div>
      ) : null}
      {analytics.estimatedPaidUsd > 0 ? (
        <div className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
          {analytics.estimatedPaidUsd} legacy paid order(s) use the current exchange rate for USD reporting.
        </div>
      ) : null}
      {/* Status Overview */}
      <Card className="p-5">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-black uppercase tracking-[0.18em] text-slate-400">Status Overview</h3>
          <span className="text-sm text-slate-600">Total items: <span className="font-semibold text-slate-900">{analytics.totalItems}</span></span>
        </div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="text-center p-4 bg-amber-50 rounded-2xl border border-amber-100">
            <p className="text-2xl font-black text-amber-700">{analytics.notApproved}</p>
            <p className="text-xs font-semibold text-amber-700">Not Approaved</p>
          </div>
          <div className="text-center p-4 bg-amber-50 rounded-2xl border border-amber-100">
            <p className="text-2xl font-black text-amber-700">{analytics.aliExpress}</p>
            <p className="text-xs font-semibold text-amber-700">AliExpress</p>
          </div>
          <div className="text-center p-4 bg-amber-50 rounded-2xl border border-amber-100">
            <p className="text-2xl font-black text-amber-700">{analytics.logestic}</p>
            <p className="text-xs font-semibold text-amber-700">Logestic</p>
          </div>
          <div className="text-center p-4 bg-emerald-50 rounded-2xl border border-emerald-100">
            <p className="text-2xl font-black text-emerald-700">{analytics.delivered}</p>
            <p className="text-xs font-semibold text-emerald-700">Delivered</p>
          </div>
        </div>
      </Card>

      {/* Search + filters */}
      <Card className="p-5">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
          <div className="lg:col-span-6 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search by Order ID, customer, email..."
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 focus:ring-2 focus:ring-primary/25 focus:border-primary text-sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="lg:col-span-3">
            <select
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:ring-2 focus:ring-primary/25 focus:border-primary text-sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Status</option>
              <option value="Not Approaved">Not Approaved</option>
              <option value="AliExpress">AliExpress</option>
              <option value="Logestic">Logestic</option>
              <option value="Delivered">Delivered</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>
          <div className="lg:col-span-3">
            <select
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:ring-2 focus:ring-primary/25 focus:border-primary text-sm"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
            >
              <option value="">All Dates</option>
              <option value="today">Today</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
            </select>
          </div>
        </div>

        <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="text-sm text-slate-600">
            Showing <span className="font-semibold text-slate-900">{filteredOrders.length}</span> of{' '}
            <span className="font-semibold text-slate-900">{totalOrders}</span> orders
            {hasActiveFilters ? (
              <span className="ml-2 text-xs text-slate-400">(filters active)</span>
            ) : null}
          </div>

          <div className="inline-flex items-center gap-2">
            <button
              type="button"
              onClick={handleClearFilters}
              disabled={!hasActiveFilters}
              className={cx(
                "px-3 py-2 rounded-xl border text-sm font-semibold transition-colors",
                hasActiveFilters
                  ? "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                  : "border-slate-100 bg-slate-50 text-slate-300 cursor-not-allowed"
              )}
            >
              Clear filters
            </button>
            <div className="inline-flex items-center rounded-2xl bg-white border border-slate-200 p-1 w-fit">
            <button
              onClick={() => setViewMode('table')}
              className={cx(
                "px-3 py-2 rounded-xl text-sm font-semibold transition-colors",
                viewMode === 'table' ? "bg-primary/10 text-primary" : "text-slate-600 hover:bg-slate-50"
              )}
            >
              Table
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={cx(
                "px-3 py-2 rounded-xl text-sm font-semibold transition-colors",
                viewMode === 'grid' ? "bg-primary/10 text-primary" : "text-slate-600 hover:bg-slate-50"
              )}
            >
              Grid
            </button>
            </div>
          </div>
        </div>
      </Card>


      {/* Orders Display */}
      {viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredOrders.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">{ordersStatus === 'loading' ? 'Loading orders…' : 'No orders found matching your criteria.'}</p>
              </div>
            ) : (
              filteredOrders.map((order) => (
                <OrderCard key={order.mongoId || order.id} order={order} />
              ))
            )}
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-4 text-left">
                    <input
                      type="checkbox"
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      checked={selectedOrders.length === filteredOrders.length && filteredOrders.length > 0}
                      className="w-4 h-4 text-primary bg-white border-slate-300 rounded focus:ring-primary"
                    />
                  </th>
                  <th className="px-6 py-4 text-left text-[11px] font-black text-slate-500 uppercase tracking-[0.18em]">Order ID</th>
                  <th className="px-6 py-4 text-left text-[11px] font-black text-slate-500 uppercase tracking-[0.18em]">Customer</th>
                  <th className="px-6 py-4 text-left text-[11px] font-black text-slate-500 uppercase tracking-[0.18em]">Date</th>
                  <th className="px-6 py-4 text-left text-[11px] font-black text-slate-500 uppercase tracking-[0.18em]">Ship To</th>
                  <th className="px-6 py-4 text-left text-[11px] font-black text-slate-500 uppercase tracking-[0.18em]">Total</th>
                  <th className="px-6 py-4 text-left text-[11px] font-black text-slate-500 uppercase tracking-[0.18em]">Status</th>
                  <th className="px-6 py-4 text-left text-[11px] font-black text-slate-500 uppercase tracking-[0.18em]">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100">
                {filteredOrders.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-12 text-gray-500">
                      <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p>{ordersStatus === 'loading' ? 'Loading orders…' : 'No orders found matching your criteria.'}</p>
                    </td>
                  </tr>
                ) : (
                  filteredOrders.map((order) => {
                    const status = statusConfig[order.status] || statusConfig.Default;
                    const StatusIcon = status?.icon || Clock;
                    const orderKey = order.mongoId || order.id;
                    const pdfState = pdfLoadingByOrderId[orderKey] || {};
                    const isViewPdfLoading = Boolean(pdfState.view);
                    const isDownloadPdfLoading = Boolean(pdfState.download);

                    return (
                      <tr key={order.mongoId || order.id} className="hover:bg-slate-50/60 transition-colors">
                        <td className="px-6 py-4">
                          <input
                            type="checkbox"
                            checked={selectedOrders.includes(order.mongoId || order.id)}
                            onChange={(e) => handleSelectOrder(order.mongoId || order.id, e.target.checked)}
                            className="w-4 h-4 text-primary bg-white border-slate-300 rounded focus:ring-primary"
                          />
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <p className="font-semibold text-slate-900">{order.id}</p>
                            <p className="text-xs text-slate-500">{order.items} items</p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <p className="font-semibold text-slate-900">{order.customer}</p>
                            <p className="text-xs text-slate-500">{order.email}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-slate-700">{order.date}</td>
                        <td className="px-6 py-4 text-slate-700">
                          <p className="font-semibold text-slate-900">{order.shipToCountry}</p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="font-semibold text-slate-900">{orderMoney(order.total, order.currency || 'USD')}</p>
                          <p className="text-xs text-slate-500">{getUsdBaseLabel(order)}</p>
                          <p className="text-xs text-slate-500">{order.currency || 'USD'} • {order.payment}</p>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${status.cls}`}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {order.status}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              className="p-2 text-slate-700 hover:bg-slate-100 rounded-xl transition-colors"
                              title="View"
                              onClick={() => goToOrderDetails(order)}
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button className="p-2 text-slate-700 hover:bg-slate-100 rounded-xl transition-colors" title="Edit">
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              className={cx(
                                "p-2 rounded-xl transition-colors",
                                isViewPdfLoading ? "text-slate-400 bg-slate-100 cursor-not-allowed" : "text-slate-700 hover:bg-slate-100"
                              )}
                              title="View PDF"
                              onClick={() => handleViewPdf(order)}
                              disabled={isViewPdfLoading}
                            >
                              {isViewPdfLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                            </button>
                            <button
                              className={cx(
                                "p-2 rounded-xl transition-colors",
                                isDownloadPdfLoading ? "text-slate-400 bg-slate-100 cursor-not-allowed" : "text-slate-700 hover:bg-slate-100"
                              )}
                              title="Download PDF"
                              onClick={() => handleDownloadPdf(order)}
                              disabled={isDownloadPdfLoading}
                            >
                              {isDownloadPdfLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                            </button>
                            <button
                              className={cx(
                                "p-2 rounded-xl transition-colors",
                                order.cancelled || cancelingOrderId === (order.mongoId || order.id)
                                  ? "text-slate-400 bg-slate-100 cursor-not-allowed"
                                  : "text-rose-600 hover:bg-rose-50"
                              )}
                              title={order.cancelled ? `Already cancelled${order.cancellationReason ? `: ${order.cancellationReason}` : ''}` : "Cancel order"}
                              onClick={() => handleCancelOrder(order)}
                              disabled={order.cancelled || cancelingOrderId === (order.mongoId || order.id)}
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
              </table>
            </div>
          </div>
        )}

    </div>
  );
};

export default AllOrders; 
